/**
 * Web Component entry point for the Community Sidebar widget.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/community-sidebar-web-component';
 *
 * // Then use in HTML:
 * // <legion-community-sidebar api-url="https://your-domain.com" />
 * ```
 */

import { SIDEBAR_THEMING_PROP_TO_PARAM } from "./types";
import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const SIDEBAR_CSS_ATTRIBUTES = Object.values(SIDEBAR_THEMING_PROP_TO_PARAM);

class LegionCommunitySidebarElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url", "title", "show-guidelines", "show-faqs", "show-tutorials",
      "show-search-links", "community-url", "guidelines-url", "faqs-url",
      "theme", "width", "height",
      ...SIDEBAR_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if ((AUTH_BRIDGE_OBSERVED_ATTRS as readonly string[]).includes(name) && this.iframe) {
      this.authBridge?.postCurrentToken();
      return;
    }
    this.render();
  }

  private get apiUrl(): string { return this.getAttribute("api-url") || window.location.origin; }
  private get widgetTitle(): string { return this.getAttribute("title") || "Getting Started"; }
  private get showGuidelines(): boolean { return this.getAttribute("show-guidelines") !== "false"; }
  private get showFaqs(): boolean { return this.getAttribute("show-faqs") !== "false"; }
  private get showTutorials(): boolean { return this.getAttribute("show-tutorials") !== "false"; }
  private get showSearchLinks(): boolean { return this.getAttribute("show-search-links") !== "false"; }
  private get communityUrl(): string { return this.getAttribute("community-url") || "/community"; }
  private get guidelinesUrl(): string { return this.getAttribute("guidelines-url") || "/guidelines"; }
  private get faqsUrl(): string { return this.getAttribute("faqs-url") || "/faqs"; }
  private get theme(): string { return this.getAttribute("theme") || "light"; }
  private get widgetWidth(): string { return this.getAttribute("width") || "100%"; }
  private get widgetHeight(): string { return this.getAttribute("height") || "auto"; }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/community-sidebar", this.apiUrl);

    url.searchParams.set("title", this.widgetTitle);
    url.searchParams.set("showGuidelines", String(this.showGuidelines));
    url.searchParams.set("showFaqs", String(this.showFaqs));
    url.searchParams.set("showTutorials", String(this.showTutorials));
    url.searchParams.set("showSearchLinks", String(this.showSearchLinks));
    url.searchParams.set("communityUrl", this.communityUrl);
    url.searchParams.set("guidelinesUrl", this.guidelinesUrl);
    url.searchParams.set("faqsUrl", this.faqsUrl);
    url.searchParams.set("theme", this.theme);

    SIDEBAR_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    const resolvedHeight = this.widgetHeight === "auto" ? "400px" : this.widgetHeight;

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = resolvedHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Community Sidebar Widget";
    iframe.loading = "lazy";

    iframe.onload = () => {
      this.dispatchEvent(new CustomEvent("load", {
        detail: { widget: "community-sidebar", theme: this.theme },
      }));
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load community sidebar widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host { display: block; width: ${this.widgetWidth.includes("%") ? this.widgetWidth : this.widgetWidth + "px"}; height: ${resolvedHeight}; }
      iframe { width: 100%; height: 100%; border: none; border-radius: 8px; }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (typeof customElements !== "undefined" && !customElements.get("legion-community-sidebar")) {
  customElements.define("legion-community-sidebar", LegionCommunitySidebarElement);
}

export { LegionCommunitySidebarElement };

